import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  Plus,
  Search,
  AlertCircle,
  ScanLine,
  Warehouse as WarehouseIcon,
  Book as BookIcon
} from 'lucide-react';
import { Warehouse } from '../../../../types/Warehouse.types';
import { Book } from '../../../../types/Book.types';
import {
  StockMovement,
  StockAuditRow,
  StockMovementType,
  STOCK_MOVEMENT_TYPE_LABELS,
  MANUAL_STOCK_MOVEMENT_TYPES,
  STOCK_MOVEMENT_SIGN
} from '../../../../types/StockMovement.types';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { useToast } from '../../../../hooks/useToast';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { listAllStockMovements, createStockMovement, buildStockAudit } from '../../../../services/StockMovementsService';
import { listWarehouses } from '../../../../services/WarehousesService';
import { listBooks } from '../../../../services/BooksService';
import { InventoryScreen } from './InventoryScreen';
import '../shared-modules.css';
import './StockAuditReportView.css';

// La pestaña activa va en ?pantalla= para que sobreviva al remontaje de la
// vista cuando se cambia de sociedad en el header.
const STOCK_AUDIT_TABS = [
  { id: 'informe', label: 'Informe_De_Auditoría', icon: ClipboardList },
  { id: 'inventario', label: 'Inventario', icon: ScanLine }
];

const today = () => new Date().toISOString().split('T')[0];

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);

interface MovementFormState {
  warehouseId: string;
  bookId: string;
  bookQuery: string;
  movementType: StockMovementType;
  quantity: string;
  movementDate: string;
}

const emptyMovementForm = (): MovementFormState => ({
  warehouseId: '',
  bookId: '',
  bookQuery: '',
  movementType: 'purchase',
  quantity: '',
  movementDate: today()
});

export const StockAuditReportView = () => {
  const { notify } = useToast();
  const location = useLocation();
  const history = useHistory();
  const tab = new URLSearchParams(location.search).get('pantalla') || 'informe';

  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [rows, setRows] = useState<StockAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [booksLoading, setBooksLoading] = useState(true);

  const [isMovementOpen, setIsMovementOpen] = useState(false);
  const [movementForm, setMovementForm] = useState<MovementFormState>(emptyMovementForm());
  const [movementError, setMovementError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const list = await listAllStockMovements();
      setMovements(list);
      setListError('');
    } catch (err) {
      console.error('Error fetching stock movements:', err);
      setMovements([]);
      setListError(err instanceof Error ? err.message : 'No se pudo cargar el informe de stock.');
    } finally {
      setLoading(false);
    }
  };

  // El informe no se puede pintar solo con los movimientos: el precio sale de
  // la ficha del libro, y sin ella Valor Stock saldría a 0 € en todas las
  // filas. Por eso books bloquea la tabla y warehouses, que solo alimenta el
  // formulario de movimiento, no.
  const fetchMasters = async () => {
    listWarehouses().then(setWarehouses).catch(e => console.error('Error fetching warehouses:', e));
    listBooks()
      .then(setBooks)
      .catch(e => console.error('Error fetching books:', e))
      .then(() => setBooksLoading(false));
  };

  useEffect(() => {
    fetchReport();
    fetchMasters();
  }, []);

  // Las filas dependen de los movimientos y del precio de la ficha del libro
  // (Valor Stock), así que se recalculan cuando cualquiera de los dos termine
  // de llegar, sin importar el orden.
  useEffect(() => {
    setRows(buildStockAudit(movements, books));
  }, [movements, books]);

  // replace y no push, como en Finanzas: el botón atrás no recorre las pestañas
  const switchTab = (next: string) => {
    if (next === tab) return;
    setIsMovementOpen(false);
    const params = new URLSearchParams(location.search);
    params.set('pantalla', next);
    history.replace({ search: `?${params.toString()}` });
  };

  const openMovementForm = () => {
    setMovementForm({ ...emptyMovementForm(), warehouseId: warehouses[0]?.id || '' });
    setMovementError('');
    setIsMovementOpen(true);
  };

  const handleSaveMovement = async () => {
    if (!movementForm.warehouseId) { setMovementError('Debe seleccionar un almacén.'); return; }
    if (!movementForm.bookId) { setMovementError('Debe seleccionar un libro de la lista de resultados.'); return; }
    if (!movementForm.movementDate) { setMovementError('La fecha del movimiento es obligatoria.'); return; }

    const qty = Number(movementForm.quantity);
    if (isNaN(qty) || qty <= 0) {
      setMovementError('Introduzca una cantidad positiva: el signo lo aplica el tipo de movimiento.');
      return;
    }

    setIsSaving(true);
    setMovementError('');
    try {
      // El usuario teclea siempre en positivo; el signo lo resuelve aquí la UI
      // según el tipo de movimiento elegido.
      await createStockMovement({
        warehouseId: movementForm.warehouseId,
        bookId: movementForm.bookId,
        movementType: movementForm.movementType,
        quantity: qty * STOCK_MOVEMENT_SIGN[movementForm.movementType],
        movementDate: movementForm.movementDate,
        sourceTable: 'manual'
      });
      setIsMovementOpen(false);
      notify('Éxito', 'Movimiento de stock registrado exitosamente', 'info');
      // Todo el informe se deriva de stock_movements, así que basta con recargar.
      await fetchReport();
    } catch (err) {
      console.error('Error creating stock movement:', err);
      setMovementError(err instanceof Error ? err.message : 'Error al registrar el movimiento.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRows = rows.filter(r =>
    r.bookTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.bookIsbn.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.warehouseName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns: ColumnDef<StockAuditRow>[] = [
    {
      id: 'warehouse',
      header: 'ALMACÉN',
      render: (row) => (
        <>
          <div className="stock-audit-view__wh">{row.warehouseName || `#${row.warehouseId}`}</div>
          <div className="stock-audit-view__sub">{row.movementCount} mov.</div>
        </>
      )
    },
    {
      id: 'book',
      header: 'OBRA / ISBN',
      render: (row) => (
        <>
          <div className="stock-audit-view__title">{row.bookTitle || `#${row.bookId}`}</div>
          <div className="stock-audit-view__sub">{row.bookIsbn || 'Sin ISBN'}</div>
        </>
      )
    },
    { id: 'openingBalance', header: 'S. INICIAL', align: 'right' },
    { id: 'purchases', header: 'COMPRAS', align: 'right' },
    { id: 'sales', header: 'VENTAS', align: 'right' },
    { id: 'adjustments', header: 'AJUSTES', align: 'right' },
    {
      id: 'currentStock',
      header: 'STOCK ACTUAL',
      align: 'right',
      render: (row) => (
        <span className={`stock-audit-view__stock ${row.currentStock < 0 ? 'negative' : ''}`}>
          {row.currentStock}
        </span>
      )
    },
    { id: 'declaredStock', header: 'STOCK DECLARADO', align: 'right' },
    { id: 'stockValue', header: 'VALOR STOCK', align: 'right', render: (row) => formatCurrency(row.stockValue) },
    { id: 'averageStock', header: 'STOCK MEDIO', align: 'right', render: (row) => row.averageStock.toFixed(2) },
    { id: 'rotationIndex', header: 'ÍND. ROTACIÓN', align: 'right', render: (row) => row.rotationIndex.toFixed(2) },
    { id: 'lastMovementDate', header: 'ÚLT. MOV.', align: 'right', render: (row) => row.lastMovementDate || '—' }
  ];

  // Buscador de libro por ISBN o título dentro del formulario.
  const bookMatches = movementForm.bookQuery.trim().length < 2
    ? []
    : books.filter(b =>
        b.title.toLowerCase().includes(movementForm.bookQuery.toLowerCase()) ||
        b.isbn.toLowerCase().includes(movementForm.bookQuery.toLowerCase())
      ).slice(0, 6);

  const selectedBook = books.find(b => b.id === movementForm.bookId);

  const totalUnits = filteredRows.reduce((acc, r) => acc + r.currentStock, 0);

  return (
    <>
      <MasterDetailLayout isOpen={isMovementOpen} drawer>
        <div className="stock-audit-view">
          <div className="module-header-row">
            <ModuleHeader
              title="Auditoría_De_Stock"
              subtitle="Reconciliación de existencias por almacén y obra, e inventarios: recuento de libros o inventario completo de tienda."
            />
          </div>

          <div className="module-tabs">
            {STOCK_AUDIT_TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" onClick={() => switchTab(id)} className={`module-tab ${tab === id ? 'active' : ''}`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          {tab === 'inventario' && <InventoryScreen onSaved={fetchReport} />}

          {tab === 'informe' && (
            <>
              <div className="module-toolbar">
                <div className="module-search-wrapper">
                  <Search className="module-search-icon" size={14} />
                  <input
                    type="text"
                    placeholder="FILTRAR POR TÍTULO, ISBN O ALMACÉN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="module-search-input"
                  />
                </div>
                <button onClick={openMovementForm} className="module-create-btn">
                  <Plus size={14} /> Registrar_Movimiento
                </button>
              </div>

              <div className="module-stats-grid">
                <div className="module-stat-card">
                  <span className="module-stat-label">LÍNEAS_DE_STOCK</span>
                  {loading || booksLoading
                    ? <Skeleton width="40px" height="1.2em" className="module-stat-value" />
                    : <span className="module-stat-value">{filteredRows.length}</span>}
                </div>
                <div className="module-stat-card">
                  <span className="module-stat-label">MOVIMIENTOS_REGISTRADOS</span>
                  {loading || booksLoading
                    ? <Skeleton width="40px" height="1.2em" className="module-stat-value" />
                    : <span className="module-stat-value">{movements.length}</span>}
                </div>
                <div className="module-stat-card">
                  <span className="module-stat-label">UNIDADES_EN_STOCK</span>
                  {loading || booksLoading
                    ? <Skeleton width="40px" height="1.2em" className="module-stat-value" />
                    : <span className="module-stat-value">{totalUnits}</span>}
                </div>
              </div>

              {/* Las filas son un informe calculado, sin ficha: no se seleccionan.
                  El panel lateral solo se abre para registrar un movimiento, y se
                  cierra con su X (esta vista no avisa de cambios sin guardar). */}

              <DataTable
                data={loading || booksLoading ? [] : filteredRows}
                columns={columns}
                title={<><ClipboardList size={14} className="module-panel-title-icon" /> Reconciliación_De_Existencias</>}
                subtitle={loading || booksLoading ? undefined : `TOTAL: ${filteredRows.length}`}
                isLoading={loading || booksLoading}
                getRowId={(row) => `${row.warehouseId}-${row.bookId}`}
                emptyText={listError || 'No hay movimientos de stock registrados.'}
              />
            </>
          )}
        </div>

        {isMovementOpen && (
          <SidebarForm
            drawer
            title="Registrar_Movimiento"
            onClose={() => setIsMovementOpen(false)}
            submitLabel={isSaving ? 'Guardando...' : 'Registrar'}
            submitDisabled={isSaving}
            onSubmit={handleSaveMovement}
          >
            <div className="stock-audit-view__sign-hint">
              Alta manual de una única línea de movimiento. El saldo inicial no se ofrece aquí: es exclusivo de la carga inicial.
            </div>

            {movementError && (
              <div className="module-form-error">
                <AlertCircle size={14} style={{ flexShrink: 0 }} />
                <span>{movementError}</span>
              </div>
            )}

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">
                <WarehouseIcon size={10} /> Almacén *
              </label>
              <select
                value={movementForm.warehouseId}
                onChange={(e) => { const v = e.target.value; setMovementForm(prev => ({ ...prev, warehouseId: v })); }}
                className="sidebar-form-input"
              >
                <option value="">— Seleccione un almacén —</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">
                <BookIcon size={10} /> Libro (ISBN o título) *
              </label>
              <input
                type="text"
                value={movementForm.bookQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  setMovementForm(prev => ({ ...prev, bookQuery: v, bookId: '' }));
                }}
                placeholder="Escriba al menos 2 caracteres..."
                className="sidebar-form-input"
              />
              {selectedBook ? (
                <div className="stock-audit-view__book-selected">
                  {selectedBook.title} · {selectedBook.isbn}
                </div>
              ) : bookMatches.length > 0 ? (
                <div className="stock-audit-view__book-results">
                  {bookMatches.map(b => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setMovementForm(prev => ({ ...prev, bookId: b.id, bookQuery: b.title }))}
                      className="stock-audit-view__book-result"
                    >
                      <span className="stock-audit-view__book-title">{b.title}</span>
                      <span className="stock-audit-view__book-isbn">{b.isbn}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="sidebar-form-grid-2">
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Tipo de Movimiento *</label>
                <select
                  value={movementForm.movementType}
                  onChange={(e) => {
                    const v = e.target.value as StockMovementType;
                    setMovementForm(prev => ({ ...prev, movementType: v }));
                  }}
                  className="sidebar-form-input"
                >
                  {MANUAL_STOCK_MOVEMENT_TYPES.map(t => (
                    <option key={t} value={t}>{STOCK_MOVEMENT_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              <div className="sidebar-form-group">
                <label className="sidebar-form-label">Cantidad (positiva) *</label>
                <input
                  type="number" min="1" step="1"
                  value={movementForm.quantity}
                  onChange={(e) => { const v = e.target.value; setMovementForm(prev => ({ ...prev, quantity: v })); }}
                  className="sidebar-form-input"
                />
              </div>
            </div>

            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Fecha del Movimiento *</label>
              <input
                type="date"
                value={movementForm.movementDate}
                onChange={(e) => { const v = e.target.value; setMovementForm(prev => ({ ...prev, movementDate: v })); }}
                className="sidebar-form-input"
              />
            </div>

            <div className="stock-audit-view__sign-hint">
              {movementForm.movementType === 'sale'
                ? 'Una venta se registrará restando del stock.'
                : 'Este movimiento se registrará sumando al stock.'}
            </div>
          </SidebarForm>
        )}
      </MasterDetailLayout>
    </>
  );
};
